// src/pages/storePages/manageStore/ProductFormDialog.jsx
import { useEffect, useRef, useState } from "react";
import {
  storeCreateProduct,
  storeUpdateProduct,
  storeGetProductBySlug,
} from "../../../storeAPIs/StoreProductsAPIs";
import { storeGetAllCategories } from "../../../storeAPIs/StoreCategoriesApis";
import { getAllProjects } from "../../../api/CoursesApi";
import ProjectPicker from "./ProjectPicker";
import "../../../static/storecss/manageStore/ProductFormDialog.css";

const EMPTY_FORM = {
  name: "",
  slug: "",
  category: "",
  price: "",
  stock: 0,
  description: "",
  warranty_months: 12,
  is_active: true,
  linked_project: "",
};

export default function ProductFormDialog({
  open,
  productSlug,
  defaultCategoryId,
  onClose,
  onSaved,
}) {
  const dialogRef = useRef(null);
  const nameRef = useRef(null);

  const isEdit = Boolean(productSlug);

  const [form, setForm] = useState(EMPTY_FORM);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [specs, setSpecs] = useState([{ key: "", value: "" }]);
  const [images, setImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
      requestAnimationFrame(() => nameRef.current?.focus());
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNewFiles([]);

    const tasks = [storeGetAllCategories(), getAllProjects()];
    if (isEdit) tasks.push(storeGetProductBySlug(productSlug));

    Promise.all(tasks)
      .then(([catsRes, projRes, prodRes]) => {
        if (cancelled) return;
        setCategories(catsRes.data?.results ?? catsRes.data ?? []);
        setProjects(projRes.data?.results ?? projRes.data ?? []);

        if (prodRes) {
          const p = prodRes.data;
          setForm({
            name: p.name ?? "",
            slug: p.slug ?? "",
            category: String(p.category?.id ?? ""),
            price: String(p.price ?? ""),
            stock: p.stock ?? 0,
            description: p.description ?? "",
            warranty_months: p.warranty_months ?? 12,
            is_active: p.is_active ?? true,
            linked_project: p.linked_project?.id
              ? String(p.linked_project.id)
              : "",
          });
          setSpecs(
            p.specs?.length
              ? p.specs.map((s) => ({ key: s.key, value: s.value }))
              : [{ key: "", value: "" }]
          );
          setImages(p.images ?? []);
        } else {
          setForm({ ...EMPTY_FORM, category: defaultCategoryId ?? "" });
          setSpecs([{ key: "", value: "" }]);
          setImages([]);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEdit, productSlug, defaultCategoryId]);

  useEffect(() => {
    if (!isEdit && form.name) {
      setForm((f) => ({
        ...f,
        slug: f.name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
      }));
    }
  }, [form.name, isEdit]);

  const updateField = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const updateSpec = (idx, key, value) =>
    setSpecs((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s))
    );

  const addSpecRow = () =>
    setSpecs((prev) => [...prev, { key: "", value: "" }]);

  const removeSpecRow = (idx) =>
    setSpecs((prev) => prev.filter((_, i) => i !== idx));

  const handleFileChange = (e) =>
    setNewFiles(Array.from(e.target.files || []));

  const handleBackdropClick = (e) => {
    if (e.target === dialogRef.current && !submitting) onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("slug", form.slug);
    fd.append("category", form.category);
    fd.append("price", form.price);
    fd.append("stock", form.stock);
    fd.append("description", form.description);
    fd.append("warranty_months", form.warranty_months);
    fd.append("is_active", form.is_active);
    fd.append("linked_project_id", form.linked_project || "");

    const cleanSpecs = specs
      .filter((s) => s.key.trim() && s.value.trim())
      .map((s) => ({ key: s.key.trim(), value: s.value.trim() }));
    fd.append("specs", JSON.stringify(cleanSpecs));

    newFiles.forEach((file, i) => {
      fd.append(`images[${i}]image`, file);
      fd.append(`images[${i}]is_primary`, i === 0 ? "true" : "false");
      fd.append(`images[${i}]alt_text`, "");
    });

    try {
      const res = isEdit
        ? await storeUpdateProduct(productSlug, fd)
        : await storeCreateProduct(fd);

      const saved = res.data?.results?.[0] ?? res.data?.data ?? res.data;
      onSaved?.(saved, isEdit ? "updated" : "created");
      onClose?.();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === "object") {
        setError(
          Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join(" · ")
        );
      } else {
        setError(err.message || "Failed to save.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="pfd-dialog"
      onClick={handleBackdropClick}
      aria-labelledby="pfd-title"
    >
      <form className="pfd-form" onSubmit={handleSubmit}>
        <header className="pfd-header">
          <div>
            <h2 id="pfd-title">{isEdit ? "Edit product" : "New product"}</h2>
            <p>
              {isEdit
                ? "Update details, specs, and images."
                : "Add a product to your store."}
            </p>
          </div>
          <button
            type="button"
            className="pfd-close"
            onClick={onClose}
            aria-label="Close dialog"
            disabled={submitting}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <div className="pfd-body">
          {loading ? (
            <div className="pfd-loading">
              <span className="pfd-spinner" />
              <span>Loading…</span>
            </div>
          ) : (
            <>
              {error && <div className="pfd-error">{error}</div>}

              <div className="pfd-field">
                <label htmlFor="pfd-name">Name</label>
                <input
                  ref={nameRef}
                  id="pfd-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="pfd-field">
                <label htmlFor="pfd-slug">
                  Slug
                  <span className="pfd-hint-inline">Used in URLs</span>
                </label>
                <input
                  id="pfd-slug"
                  type="text"
                  value={form.slug}
                  onChange={(e) => updateField("slug", e.target.value)}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="pfd-field">
                <label htmlFor="pfd-cat">Category</label>
                <select
                  id="pfd-cat"
                  value={form.category}
                  onChange={(e) => updateField("category", e.target.value)}
                  required
                >
                  <option value="">— Select a category —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pfd-field">
                <label>Linked course (optional)</label>
                <ProjectPicker
                  projects={projects}
                  value={form.linked_project}
                  onChange={(id) => updateField("linked_project", id)}
                />
              </div>

              <div className="pfd-grid-3">
                <div className="pfd-field">
                  <label htmlFor="pfd-price">Price (KES)</label>
                  <input
                    id="pfd-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => updateField("price", e.target.value)}
                    required
                  />
                </div>
                <div className="pfd-field">
                  <label htmlFor="pfd-stock">Stock</label>
                  <input
                    id="pfd-stock"
                    type="number"
                    min="0"
                    value={form.stock}
                    onChange={(e) => updateField("stock", e.target.value)}
                  />
                </div>
                <div className="pfd-field">
                  <label htmlFor="pfd-warranty">Warranty</label>
                  <input
                    id="pfd-warranty"
                    type="number"
                    min="0"
                    value={form.warranty_months}
                    onChange={(e) =>
                      updateField("warranty_months", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="pfd-field">
                <label htmlFor="pfd-desc">Description</label>
                <textarea
                  id="pfd-desc"
                  rows="3"
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                />
              </div>

              <label className="pfd-check">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                />
                <span>Active — visible in store</span>
              </label>

              <fieldset className="pfd-fieldset">
                <legend>Specifications</legend>

              <div className="pfd-spec-hint" role="note">
                <div className="pfd-spec-hint-head">
                  <i className="fa-solid fa-circle-info" aria-hidden="true" />
                  <span>Multi-line values</span>
                </div>

                <p className="pfd-spec-hint-desc">
                  Start each line with <code>-&gt;</code> to turn it into a bullet on the
                  product page.
                </p>
              </div>

                {specs.map((s, i) => (
                  <div key={i} className="pfd-spec-row">
                    <input
                      type="text"
                      placeholder="Key"
                      value={s.key}
                      onChange={(e) => updateSpec(i, "key", e.target.value)}
                    />
                    <textarea
                      rows="1"
                      placeholder="Value — use -> for bullets, one per line"
                      value={s.value}
                      onChange={(e) =>
                        updateSpec(i, "value", e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="pfd-spec-remove"
                      onClick={() => removeSpecRow(i)}
                      aria-label="Remove specification"
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="pfd-btn pfd-btn--ghost pfd-btn--sm"
                  onClick={addSpecRow}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                  <span>Add specification</span>
                </button>
              </fieldset>

              <fieldset className="pfd-fieldset">
                <legend>Images</legend>
                {isEdit && images.length > 0 && (
                  <div className="pfd-image-strip">
                    {images.map((img) => (
                      <img
                        key={img.id}
                        src={img.image_url}
                        alt={img.alt_text || ""}
                      />
                    ))}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                />
                <p className="pfd-hint">
                  {isEdit
                    ? "Uploading new images replaces the existing set."
                    : "First image is used as the primary."}
                </p>
              </fieldset>
            </>
          )}
        </div>

        <footer className="pfd-footer">
          <button
            type="button"
            className="pfd-btn pfd-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pfd-btn pfd-btn--primary"
            disabled={submitting || loading}
          >
            {submitting
              ? "Saving…"
              : isEdit
              ? "Save changes"
              : "Create product"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}